import { generateS3SubmittingUrlMulti, insertDocumentsMulti } from '@/api'

export const normalizeFeatureName = (name) => {
  if (!name) return ''
  return name.replace(/\s/g, '').split('.')[0].toLowerCase()
}

export const getRequiredExtension = (folder) => {
  if (!folder?.blob_key) return null
  const parts = folder.blob_key.split('.')
  return parts.length > 1 ? parts.pop() : null
}

export const splitFileName = (fileName) => {
  const lastDot = fileName.lastIndexOf('.')
  if (lastDot === -1) {
    return { baseName: fileName, extension: '' }
  }
  return {
    baseName: fileName.slice(0, lastDot),
    extension: fileName.slice(lastDot + 1)
  }
}

export const getUploadFeatureInfo = ({ isRevision, folder, parentFolder, fileNameBase }) => {
  const featureSource = isRevision
    ? (parentFolder?.doc_name ?? folder?.doc_name)
    : folder?.doc_name
  const featureIdSource = isRevision ? folder?.doc_name : fileNameBase
  return {
    feature: normalizeFeatureName(featureSource),
    featureId: normalizeFeatureName(featureIdSource)
  }
}

export const filterFilesByExtension = (files, requiredExtension) => {
  if (!requiredExtension) {
    return { valid: files, invalid: [] }
  }
  const valid = []
  const invalid = []
  const requiredLower = requiredExtension.toLowerCase()
  for (const file of files) {
    const { extension } = splitFileName(file.name)
    if (extension.toLowerCase() === requiredLower) {
      valid.push(file)
    } else {
      invalid.push(file)
    }
  }
  return { valid, invalid }
}

export const filterDuplicateFiles = (files, allDocuments = []) => {
  const existingNames = new Set(allDocuments.map((doc) => doc.doc_name))
  const seen = new Set()
  const unique = []
  const duplicates = []
  for (const file of files) {
    if (existingNames.has(file.name) || seen.has(file.name)) {
      duplicates.push(file)
    } else {
      unique.push(file)
      seen.add(file.name)
    }
  }
  return { unique, duplicates }
}

export const uploadFilesToS3 = async ({ tenantId, projectId, feature, featureId, files }) => {
  const fileNames = files.map((file) => file.name)
  const payload = { tenantId, feature, featureId, fileNames }
  if (projectId) {
    payload.projectId = projectId
  }
  const { url: urlEntries } = await generateS3SubmittingUrlMulti(payload)
  const urlMap = new Map((urlEntries ?? []).map((entry) => [entry.fileName, entry.url]))

  const uploaded = await Promise.all(
    files.map(async (file) => {
      const url = urlMap.get(file.name)
      if (!url) {
        return null
      }
      await fetch(url, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
      const blobKey = decodeURIComponent(url.split('?')[0].split('.com/').pop())
      return { file, blobKey, url }
    })
  )

  return {
    uploaded: uploaded.filter(Boolean),
    missingUrls: files.filter((file) => !urlMap.has(file.name))
  }
}

export const uploadMultipleDocuments = async ({ tenantId, projectId, feature, featureId, files, parentId, versionOf }) => {
  const { uploaded, missingUrls } = await uploadFilesToS3({
    tenantId,
    projectId,
    feature,
    featureId,
    files
  })

  const insertObjs = uploaded.map(({ file, blobKey }) => {
    const { extension } = splitFileName(file.name)
    return {
      blob_key: blobKey,
      description: '',
      doc_name: file.name,
      doc_size: file.size,
      doc_type: null,
      folder: false,
      parent_id: parentId,
      version_of: versionOf,
      doc_ext: extension
    }
  })

  let newDocIds = []
  if (insertObjs.length) {
    const res = await insertDocumentsMulti(insertObjs)
    newDocIds = res?.insert_core_documents?.returning?.map((doc) => doc.id) || []
  }

  return { newDocIds, missingUrls }
}
